import { memo, useMemo } from 'react';
import {
    Sidebar as ProSidebar,
    Menu,
    MenuItem,
    MenuItemStyles,
    MenuItemStylesParams,
} from 'react-pro-sidebar';
import { NavLink, useLocation } from 'react-router-dom';

import { Divider, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

// font icons and styles
import { getSecondaryColor, getTransparentColor } from '@modules/theme';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AodOutlinedIcon from '@mui/icons-material/AodOutlined';

import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import PollOutlinedIcon from '@mui/icons-material/PollOutlined';
import AppShortcutIcon from '@mui/icons-material/AppShortcut';
import SpeakerNotes from '@mui/icons-material/SpeakerNotes';

import LogoutFooter from './LogoutFooter';
import RouterOutletContents from './RouterOutletContents';
import SwitchInterviewSidebarSelector from 'sidebarSelector/SwitchInterviewSidebarSelector';
import UserInfo from '@components/UserInfo';
import useIsTallScreen from '@hooks/isTallScreen';
import { useIsAccessAdmin } from './FirebaseAuthentication';
import ScreenLockPortraitOutlined from '@mui/icons-material/ScreenLockPortraitOutlined';
import SidebarLogo from './SidebarLogo';

const inactiveColor = getSecondaryColor();

function SidebarMenu({ visible }: { visible: boolean }) {
    const { palette } = useTheme();
    const location = useLocation();

    const isActiveRoute = (path: string) => location.pathname.includes(path);

    const menuItemStyles: MenuItemStyles = useMemo(
        () => ({
            button: ({ level, active }: MenuItemStylesParams) => {
                // only apply styles on first level elements of the tree
                if (level === 0)
                    return {
                        backgroundColor: active
                            ? getTransparentColor(palette.primary.main, 0.3)
                            : '#fff',
                        color: inactiveColor,
                        height: 'calc(1.5rem + 3vh)',
                        padding: '1vh .5rem',
                        '&:hover': {
                            backgroundColor: getTransparentColor(palette.primary.main, 0.2),
                        },
                    };
            },
        }),
        [palette.primary.main],
    );

    const isTallScreen = useIsTallScreen();

    const isAccessAdmin = useIsAccessAdmin();

    const iconProps = useMemo(
        () => ({ fontSize: isTallScreen ? ('medium' as const) : ('small' as const) }),
        [isTallScreen],
    );

    const textProps = useMemo(
        () => ({ fontSize: isTallScreen ? ('1rem' as const) : ('.7rem' as const) }),
        [isTallScreen],
    );

    // const classes = useStyles({ isTallScreen });

    return (
        // The signed-in app is a viewport-height frame: the sidebar is pinned and
        // RouterOutletContents scrolls inside it. This used to wrap every route in
        // AppRoutes, which clipped the public pages at 100vh with no way to scroll.
        <div style={{ display: 'flex', height: '100vh', width: '100%', overflow: 'hidden' }}>
            <ProSidebar
                customBreakPoint="1200px"
                backgroundColor="#fff"
                width={'250px'}
                // 100% of the frame above, not 100vh again: two elements claiming the
                // viewport is how the public pages ended up clipped in the first place.
                rootStyles={{ height: '100%', overflow: 'hidden' }}
                style={{
                    borderRightWidth: 0.3,
                    zIndex: 1200,
                    borderColor: palette.secondary.main,
                    // Animate the Sidebar mounting
                    transition: '0.6s',
                    ...(visible ? {} : { marginLeft: '-250px' }),
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        // padding: '.5rem 0 1rem 0',
                    }}
                >
                    <SidebarLogo />
                    <div style={{ flex: 1, overflowY: 'auto' }}>
                        <Menu menuItemStyles={menuItemStyles}>
                            {visible ? <SwitchInterviewSidebarSelector /> : null}
                            <Divider sx={{ marginTop: '1vh' }} />
                            <MenuItem
                                component={<NavLink to="survey" />}
                                active={isActiveRoute('survey')}
                                icon={<EditOutlinedIcon {...iconProps} />}
                            >
                                <Typography {...textProps}>Survey Editor</Typography>
                            </MenuItem>
                            <MenuItem
                                component={<NavLink to="access-manager" />}
                                active={isActiveRoute('access-manager')}
                                icon={<AdminPanelSettingsOutlinedIcon {...iconProps} />}
                            >
                                <Typography {...textProps}>User Management</Typography>
                            </MenuItem>
                            <MenuItem
                                component={<NavLink to="devices" />}
                                active={isActiveRoute('devices')}
                                icon={<AodOutlinedIcon {...iconProps} />}
                            >
                                <Typography {...textProps}>Devices</Typography>
                            </MenuItem>
                            <MenuItem
                                component={<NavLink to="results" />}
                                active={isActiveRoute('results')}
                                icon={<PollOutlinedIcon {...iconProps} />}
                            >
                                <Typography {...textProps}>Results</Typography>
                            </MenuItem>
                            <MenuItem
                                component={<NavLink to="access-pin" />}
                                active={isActiveRoute('access-pin')}
                                icon={<ScreenLockPortraitOutlined {...iconProps} />}
                            >
                                <Typography {...textProps}>Access PIN</Typography>
                            </MenuItem>
                            <MenuItem
                                component={<NavLink to="mobile-app" />}
                                active={isActiveRoute('mobile-app')}
                                icon={<AppShortcutIcon {...iconProps} />}
                            >
                                <Typography {...textProps}>Mobile App</Typography>
                            </MenuItem>

                            {/* Only show user access requests to admins that can approve them */}
                            {isAccessAdmin ? (
                                <MenuItem
                                    component={<NavLink to="access-requests" />}
                                    active={isActiveRoute('access-requests')}
                                    icon={<SpeakerNotes {...iconProps} />}
                                >
                                    <Typography {...textProps}>Access Requests</Typography>
                                </MenuItem>
                            ) : null}
                        </Menu>
                    </div>

                    {/* Footer section - always at bottom */}
                    <div style={{ marginTop: 'auto', paddingTop: '1.2rem' }}>
                        <UserInfo />
                        <LogoutFooter />
                    </div>
                </div>
            </ProSidebar>

            <RouterOutletContents />
        </div>
    );
}

export default memo(SidebarMenu);
